import { useState, useEffect } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';

const cookieTexts = {
  en: { message: 'We use cookies to enhance your browsing experience.', accept: 'Accept', reject: 'Reject', privacy: 'Privacy Policy', terms: 'Terms & Conditions' },
  it: { message: 'Utilizziamo i cookie per migliorare la tua esperienza di navigazione.', accept: 'Accetta', reject: 'Rifiuta', privacy: 'Privacy Policy', terms: 'Termini e Condizioni' },
  es: { message: 'Utilizamos cookies para mejorar tu experiencia de navegación.', accept: 'Aceptar', reject: 'Rechazar', privacy: 'Política de Privacidad', terms: 'Términos y Condiciones' },
  de: { message: 'Wir verwenden Cookies, um Ihr Surferlebnis zu verbessern.', accept: 'Akzeptieren', reject: 'Ablehnen', privacy: 'Datenschutz', terms: 'Nutzungsbedingungen' },
  pt: { message: 'Utilizamos cookies para melhorar a sua experiência de navegação.', accept: 'Aceitar', reject: 'Rejeitar', privacy: 'Política de Privacidade', terms: 'Termos e Condições' },
  ru: { message: 'Мы используем файлы cookie для улучшения вашего опыта.', accept: 'Принять', reject: 'Отклонить', privacy: 'Политика конфиденциальности', terms: 'Условия использования' },
  zh: { message: '我们使用 Cookie 来改善您的浏览体验。', accept: '接受', reject: '拒绝', privacy: '隐私政策', terms: '条款与条件' },
  ja: { message: 'ブラウジング体験を向上させるためにCookieを使用しています。', accept: '同意', reject: '拒否', privacy: 'プライバシーポリシー', terms: '利用規約' },
  nl: { message: 'Wij gebruiken cookies om uw browse-ervaring te verbeteren.', accept: 'Accepteren', reject: 'Weigeren', privacy: 'Privacybeleid', terms: 'Voorwaarden' },
  fr: { message: 'Nous utilisons des cookies pour améliorer votre expérience de navigation.', accept: 'Accepter', reject: 'Refuser', privacy: 'Politique de confidentialité', terms: 'Conditions d\'utilisation' },
};

const CookieConsent = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const texts = cookieTexts[language] || cookieTexts.en;

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookie_consent', 'rejected');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
        >
          <div className="mx-auto max-w-4xl rounded-xl border border-border/50 bg-background/95 backdrop-blur-md p-5 shadow-2xl shadow-background/30">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3 md:items-center">
                <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div className="text-sm text-muted-foreground">
                  <p>{texts.message}</p>
                  <div className="mt-1 flex gap-3 text-xs">
                    <Link to="/privacy" className="text-primary underline underline-offset-2 hover:text-primary/80">
                      {texts.privacy}
                    </Link>
                    <Link to="/terms" className="text-primary underline underline-offset-2 hover:text-primary/80">
                      {texts.terms}
                    </Link>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={handleReject}
                  className="rounded-lg border border-border px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary"
                >
                  {texts.reject}
                </button>
                <button
                  onClick={handleAccept}
                  className="rounded-lg bg-primary px-4 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  {texts.accept}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
